import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import PhotoSection from '../components/PhotoSection';
import AiIdentifyPanel from '../components/AiIdentifyPanel';
import AiCreditsBadge from '../components/credits/AiCreditsBadge';
import DeleteAccountModal from '../components/DeleteAccountModal';
import { PRIVACY_POLICY_URL, isExternalPolicy } from '../utils/privacy';
import { errorKey, ResendVerification } from '../components/RegisterPanel';
import OtpEntry from '../components/auth/OtpEntry';
import Turnstile from '../components/auth/Turnstile';
import { MIN_PASSWORD_LENGTH } from '../utils/passwordRules';

const SUBSTRATES = ['live_trunk', 'dead_trunk', 'live_leaf', 'dead_leaf', 'dung', 'roots', 'soil', 'on_another_fungus', 'carcass', 'other'];
// Raw fruiting_body_size Postgres ENUM values — must match Collections.js/External.js.
const SIZES = ['under_1cm', '1_to_2cm', '2_to_5cm', '5_to_10cm', 'over_10cm'];
const GROWTH_PATTERNS = ['single', 'clustered', 'spread'];
const STAGES = ['young', 'open', 'mature', 'old'];
const RANKS = ['species', 'genus', 'family'];

const box = { maxWidth: 480, margin: '0 auto', padding: '24px 16px', fontFamily: 'sans-serif' };
const input = { display: 'block', width: '100%', padding: 10, marginBottom: 12, border: '1px solid var(--c-border-strong)', borderRadius: 8, boxSizing: 'border-box' };
const label = { display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 };
const btn = (bg) => ({ display: 'block', width: '100%', padding: 12, background: bg, color: 'var(--c-on-accent)', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 8 });

export default function Survey() {
  const { t, i18n } = useTranslation('survey');
  const { user, login, logout } = useAuth();
  const isPublic = user?.roles?.includes('public');
  const { t: tAccount } = useTranslation('account');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const accountDeleted = new URLSearchParams(window.location.search).get('accountDeleted') === '1';

  if (user && !isPublic) {
    return (
      <div style={box}>
        <p>{t('wrongRole')}</p>
        <a href="/">{t('goToApp')}</a>
      </div>
    );
  }

  return (
    <div style={box}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        {/* What is left to ask the AI this month (routes/ai_credits.py). */}
        {isPublic && <AiCreditsBadge user={user} />}
        {isPublic && (
          // In the page shell, not on the thank-you screen: App Store 5.1.1(v)
          // needs deletion to be findable, and a control that appears only
          // after a submission is not.
          <button type="button" onClick={() => setDeleteOpen(true)} title={t('tip.deleteAccount')} style={{ border: '1px solid var(--c-danger-border)', borderRadius: 6, background: 'var(--c-surface)', color: 'var(--c-danger-2-fg)', padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>
            {tAccount('deleteAccount')}
          </button>
        )}
        <button onClick={() => i18n.changeLanguage(i18n.language === 'en' ? 'he' : 'en')} title={t('tip.language')} style={{ border: '1px solid var(--c-border-strong)', borderRadius: 6, background: 'var(--c-surface)', padding: '4px 10px', cursor: 'pointer' }}>
          {i18n.language === 'en' ? 'עב' : 'EN'}
        </button>
      </div>
      <h1 style={{ fontSize: 20, marginBottom: 20 }}>{t('title')}</h1>
      {accountDeleted && !isPublic && (
        <p role="status" style={{ color: 'var(--c-success-3-fg)', background: 'var(--c-primary-soft-2)', border: '1px solid var(--c-primary-border)', borderRadius: 8, padding: '10px 12px', fontSize: 14 }}>
          {tAccount('accountDeleted')}
        </p>
      )}
      {/* A live field event's guests join with its code (Part III; pages/events/EventJoin.js). */}
      <p style={{ marginBottom: 16, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <Link to="/join" data-testid="survey-join-event">{t('joinEvent')}</Link>
        {/* Field Mode in Observation mode (public portal 2a): the camera, the
            map-free gallery and the outbox, for a signed-in public account. */}
        {isPublic && (
          <Link to="/field" data-testid="survey-field-mode" title={t('tip.fieldMode')}>{t('fieldMode')}</Link>
        )}
        {/* The public Biobank catalog (public portal 2b). */}
        {isPublic && (
          <Link to="/catalog" data-testid="survey-catalog" title={t('tip.catalog')}>{t('catalog')}</Link>
        )}
        {/* Published records (public portal 2c). */}
        {isPublic && (
          <Link to="/records" data-testid="survey-records" title={t('tip.records')}>{t('records')}</Link>
        )}
      </p>
      {isPublic ? <SightingWizard onLogout={logout} /> : <AuthPanel onLogin={login} />}
      {deleteOpen && <DeleteAccountModal returnTo="/survey" onClose={() => setDeleteOpen(false)} />}
    </div>
  );
}

function AuthPanel({ onLogin }) {
  const { t } = useTranslation('survey');
  const [mode, setMode] = useState('register'); // 'register' | 'login'
  const { t: tAccount } = useTranslation('account');
  const { t: tReg } = useTranslation('register');
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  // 0107: the CAPTCHA's token, the code step once sent, and an address that
  // signed in before typing its code.
  const [captchaToken, setCaptchaToken] = useState(null);
  const [sent, setSent] = useState(null);   // { email, instant }
  const [unverified, setUnverified] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError(''); setInfo(''); setUnverified(''); setLoading(true);
    try {
      if (mode === 'register') {
        // Checked here as well as by `required`, and enforced again by the
        // server, which records the policy version agreed to.
        if (!acceptPrivacy) { setError(tAccount('consentRequired')); return; }
        const res = await api.post('/auth/register-public', {
          full_name: fullName, email, password, accept_privacy: true,
          ...(captchaToken ? { captcha_token: captchaToken } : {}),
        });
        // The code they are mailed lets them straight in when the CAPTCHA passed.
        setSent({ email: email.trim(), instant: res.data?.instant_access === true });
      } else {
        const res = await api.post('/auth/login', { email, password });
        onLogin(res.data.token);
      }
    } catch (err) {
      const status = err.response?.status;
      const code = err.response?.data?.code;
      // Distinguished by CODE: since migration 0017 a 403 can mean "verify
      // your email" as well as "awaiting approval", and the two need
      // different next steps.
      if (code === 'email_not_verified') { setError(tReg('notVerified')); setUnverified(email.trim()); }
      else if (status === 403) setError(t('pendingApproval'));
      else if (mode === 'register' && code) setError(tReg(errorKey(code)));
      else setError(t('loginFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <OtpEntry email={sent.email} instantAccess={sent.instant} onSignedIn={onLogin}
        onBack={() => { setSent(null); setMode('login'); }} backLabel={t('loginTab')} />
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <button onClick={() => setMode('register')} title={t('tip.registerTab')} style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid var(--c-primary)', background: mode === 'register' ? 'var(--c-primary)' : 'var(--c-surface)', color: mode === 'register' ? 'var(--c-on-accent)' : 'var(--c-primary-fg)', cursor: 'pointer' }}>{t('registerTab')}</button>
        <button onClick={() => setMode('login')} title={t('tip.loginTab')} style={{ flex: 1, padding: 8, borderRadius: 6, border: '1px solid var(--c-primary)', background: mode === 'login' ? 'var(--c-primary)' : 'var(--c-surface)', color: mode === 'login' ? 'var(--c-on-accent)' : 'var(--c-primary-fg)', cursor: 'pointer' }}>{t('loginTab')}</button>
      </div>
      <form onSubmit={submit}>
        {mode === 'register' && (
          <>
            <label style={label}>{t('fullName')}</label>
            <input style={input} value={fullName} onChange={e => setFullName(e.target.value)} required title={t('tip.fullName')} />
          </>
        )}
        <label style={label}>{t('email')}</label>
        <input type="email" style={input} value={email} onChange={e => setEmail(e.target.value)} required title={t('tip.email')} />
        <label style={label}>{t('password')}</label>
        <input type="password" style={input} value={password} onChange={e => setPassword(e.target.value)} required minLength={mode === 'register' ? MIN_PASSWORD_LENGTH : undefined}
          title={mode === 'register' ? t('tip.passwordNew', { min: MIN_PASSWORD_LENGTH }) : t('tip.password')} />
        {mode === 'register' && (
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, margin: '4px 0 12px' }}>
            <input type="checkbox" checked={acceptPrivacy} onChange={e => setAcceptPrivacy(e.target.checked)} required title={t('tip.privacy')} />
            <span>
              {tAccount('consentPrefix')}
              <a href={PRIVACY_POLICY_URL} target={isExternalPolicy() ? '_blank' : undefined} rel="noopener noreferrer">{tAccount('privacyPolicy')}</a>
            </span>
          </label>
        )}
        {mode === 'register' && <Turnstile onToken={setCaptchaToken} />}
        {error && <p style={{ color: 'var(--c-danger-2-fg)' }}>{error}</p>}
        {info && <p style={{ color: 'var(--c-primary-fg)' }}>{info}</p>}
        <button type="submit" disabled={loading} style={btn('var(--c-primary)')}
          title={mode === 'register' ? t('tip.register') : t('tip.login')}>{t(mode === 'register' ? 'register' : 'login')}</button>
      </form>
      {unverified && <ResendVerification email={unverified} onSignedIn={onLogin} />}
    </div>
  );
}

const emptyForm = {
  gps_lat: '', gps_lng: '', region: '',
  substrate: '', fruiting_body_size: '', fruiting_body_count: '', growth_pattern: '', phenological_stage: '',
  current_rank: '', current_taxon_name: '', current_confidence: '',
  general_notes: '',
};

/*
 * "Identify with AI" on the photo step: the draft's own photographs, by id
 * (POST /ai/identify {photo_ids}; routes/ai.py lets a public account ask only
 * about its own sighting while it is a draft), paid for in AI credits
 * (routes/ai_credits.py). A name chosen here becomes the sighting's proposed
 * name, marked uncertain; a dangerous look-alike is never offered to choose
 * (AiIdentifyPanel's pickAny rule).
 */
export function SurveyAi({ sampleId, chosen, onChoose }) {
  const { t } = useTranslation('survey');
  const [photoIds, setPhotoIds] = useState(null);
  const [none, setNone] = useState(false);

  const start = () => Promise.resolve()
    .then(() => api.get(`/collections/${sampleId}/photos`))
    .then(res => {
      const ids = (res?.data || []).map(p => p.photo_id).filter(Boolean).slice(0, 5);
      setNone(ids.length === 0);
      setPhotoIds(ids.length ? ids : null);
    })
    .catch(() => setNone(true));

  return (
    <section style={{ border: '1px solid var(--c-border)', borderRadius: 8, padding: 12, marginTop: 12 }}
      aria-labelledby="survey-ai-title" data-testid="survey-ai">
      <h3 id="survey-ai-title" style={{ fontSize: 15, margin: '0 0 4px' }}>{t('ai.title')}</h3>
      <p style={{ fontSize: 13, color: 'var(--c-text-muted-fg)', margin: '0 0 8px' }}>{t('ai.help')}</p>
      {!photoIds && (
        <button type="button" style={{ ...btn('var(--c-surface)'), color: 'var(--c-primary-fg)', border: '1px solid var(--c-primary)' }}
          title={t('tip.ai')} data-testid="survey-ai-start" onClick={start}>
          {t('ai.start')}
        </button>
      )}
      {none && <p role="status" style={{ fontSize: 13 }} data-testid="survey-ai-no-photos">{t('ai.noPhotos')}</p>}
      {photoIds && (
        <AiIdentifyPanel key={photoIds.join(',')} photoIds={photoIds} autoRun pickAny compact
          picked={chosen} onAccept={onChoose} />
      )}
      {chosen && photoIds && (
        <p role="status" style={{ fontSize: 13, marginTop: 8 }} data-testid="survey-ai-chosen">
          {t('ai.chosen', { name: chosen })}
        </p>
      )}
    </section>
  );
}

function SightingWizard({ onLogout }) {
  // 'survey' stays the default namespace (unprefixed t() calls below); the
  // 'collections' namespace is loaded only for its ENUM-value labels, which
  // are already translated there and keyed by the exact raw ENUM values.
  const { t } = useTranslation(['survey', 'collections']);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(emptyForm);
  const [sampleId, setSampleId] = useState(null);
  const [gpsStatus, setGpsStatus] = useState('idle'); // idle | capturing | done | error
  const [regionQuery, setRegionQuery] = useState('');
  const [regionResults, setRegionResults] = useState([]);
  const [vegetation, setVegetation] = useState([]);
  const [vegInput, setVegInput] = useState('');
  const [identifyChoice, setIdentifyChoice] = useState(null); // 'propose' | 'unknown' | null
  const [taxonQuery, setTaxonQuery] = useState('');
  const [taxonSuggestions, setTaxonSuggestions] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (field) => (e) => setForm(f => ({ ...f, [field]: e.target.value }));

  const captureGps = () => {
    if (!navigator.geolocation) { setGpsStatus('error'); return; }
    setGpsStatus('capturing');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(f => ({ ...f, gps_lat: pos.coords.latitude, gps_lng: pos.coords.longitude }));
        setGpsStatus('done');
      },
      () => setGpsStatus('error'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const searchRegion = (val) => {
    setRegionQuery(val);
    if (val.trim().length < 2) { setRegionResults([]); return; }
    api.get(`/regions/search?q=${encodeURIComponent(val.trim())}`)
      .then(res => setRegionResults(res.data))
      .catch(() => {});
  };

  const searchTaxon = (val) => {
    setTaxonQuery(val);
    setForm(f => ({ ...f, current_taxon_name: val }));
    if (val.trim().length < 2 || !form.current_rank) { setTaxonSuggestions([]); return; }
    api.get(`/taxonomy/search?q=${encodeURIComponent(val.trim())}&rank=${form.current_rank}`)
      .then(res => setTaxonSuggestions(res.data))
      .catch(() => {});
  };

  const ensureDraft = async () => {
    if (sampleId) return sampleId;
    const res = await api.post('/collections', {});
    setSampleId(res.data.sample_id);
    return res.data.sample_id;
  };

  const saveDraftFields = async (id, fields) => {
    const payload = Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== '' && v !== null && v !== undefined));
    if (Object.keys(payload).length === 0) return;
    await api.put(`/collections/${id}`, payload);
  };

  const addVegetation = async () => {
    if (!vegInput.trim()) return;
    setError('');
    try {
      const id = await ensureDraft();
      await api.post(`/collections/${id}/vegetation`, { species: vegInput.trim(), is_dominant: vegetation.length === 0 });
      setVegetation(v => [...v, vegInput.trim()]);
      setVegInput('');
    } catch (err) {
      const missing = err.response?.data?.missing;
      setError(missing ? `${t('missingFields')} (${missing.join(', ')})` : t('submitError'));
    }
  };

  const canAdvance = () => {
    if (step === 0) return !!form.gps_lat && !!form.gps_lng && !!form.substrate && vegetation.length > 0;
    if (step === 1) return !!form.fruiting_body_size && form.fruiting_body_count !== '' && !!form.growth_pattern && !!form.phenological_stage;
    if (step === 2) {
      if (identifyChoice === 'unknown') return true;
      if (identifyChoice === 'propose') return !!form.current_rank && !!form.current_taxon_name && !!form.current_confidence;
      return false;
    }
    return true;
  };

  const next = async () => {
    setError('');
    try {
      const id = await ensureDraft();
      if (step === 0) {
        await saveDraftFields(id, { gps_lat: form.gps_lat, gps_lng: form.gps_lng, region: form.region, substrate: form.substrate });
      }
      if (step === 1) {
        await saveDraftFields(id, {
          fruiting_body_size: form.fruiting_body_size,
          fruiting_body_count: form.fruiting_body_count,
          growth_pattern: form.growth_pattern,
          phenological_stage: form.phenological_stage,
        });
      }
      if (step === 2) {
        if (identifyChoice === 'unknown') {
          await saveDraftFields(id, { current_rank: 'unknown', current_taxon_name: 'Unknown', current_confidence: 'uncertain' });
        } else {
          await saveDraftFields(id, {
            current_rank: form.current_rank,
            current_taxon_name: form.current_taxon_name,
            current_confidence: form.current_confidence,
          });
        }
      }
      setStep(s => s + 1);
    } catch (err) {
      const missing = err.response?.data?.missing;
      setError(missing ? `${t('missingFields')} (${missing.join(', ')})` : t('submitError'));
    }
  };

  const submitFinal = async () => {
    setError(''); setSubmitting(true);
    try {
      await saveDraftFields(sampleId, { general_notes: form.general_notes });
      await api.put(`/collections/${sampleId}/submit`);
      setDone(true);
    } catch (err) {
      const missing = err.response?.data?.missing;
      setError(missing ? `${t('missingFields')} (${missing.join(', ')})` : t('submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  const reportAnother = () => {
    setForm(emptyForm); setSampleId(null); setStep(0); setVegetation([]);
    setIdentifyChoice(null); setDone(false); setGpsStatus('idle');
  };

  if (done) {
    return (
      <div>
        <p>{t('thankYou')}</p>
        {/* Handing the specimen to the lab (public portal 2d): the request is made
            from the sighting's page in Field Mode. */}
        {sampleId && (
          <p style={{ fontSize: 14 }}>
            <Link to={`/field/collection/${sampleId}`} data-testid="survey-hand-in" title={t('tip.handIn')}>
              {t('handIn')}
            </Link>
          </p>
        )}
        <button style={btn('var(--c-primary)')} title={t('tip.reportAnother')} onClick={reportAnother}>{t('reportAnother')}</button>
        <button style={{ ...btn('var(--c-surface)'), color: 'var(--c-primary-fg)', border: '1px solid var(--c-primary)' }} title={t('tip.goToApp')} onClick={onLogout}>{t('login')} / {t('goToApp')}</button>
      </div>
    );
  }

  const steps = ['location', 'observation', 'identification', 'photos', 'notes'];

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {steps.map((s, i) => (
          <div key={s} style={{ flex: 1, height: 4, borderRadius: 2, background: i <= step ? 'var(--c-primary)' : 'var(--c-border)' }} />
        ))}
      </div>
      <h2 style={{ fontSize: 16 }}>{t(`step.${steps[step]}`)}</h2>

      {step === 0 && (
        <div>
          <button type="button" style={btn('var(--c-primary)')} title={t('tip.captureGps')} onClick={captureGps}>
            {gpsStatus === 'capturing' ? t('gpsCapturing') : t('captureGps')}
          </button>
          {gpsStatus === 'error' && <p style={{ color: 'var(--c-danger-2-fg)', fontSize: 13 }}>{t('gpsFailed')}</p>}
          {gpsStatus === 'done' && <p style={{ fontSize: 13, color: 'var(--c-primary-fg)' }}>{form.gps_lat}, {form.gps_lng}</p>}
          <label style={label}>{t('manualLat')}</label>
          <input style={input} type="number" step="any" value={form.gps_lat} onChange={set('gps_lat')} title={t('tip.lat')} />
          <label style={label}>{t('manualLng')}</label>
          <input style={input} type="number" step="any" value={form.gps_lng} onChange={set('gps_lng')} title={t('tip.lng')} />

          <label style={label}>{t('regionSearch')}</label>
          <input style={input} value={regionQuery} onChange={e => searchRegion(e.target.value)} title={t('tip.regionSearch')} />
          {regionResults.length > 0 && (
            <ul style={{ listStyle: 'none', padding: 0, marginTop: -8, marginBottom: 12 }}>
              {regionResults.map(r => (
                <li key={r.location_id}>
                  <button type="button" style={{ background: 'none', border: 'none', color: 'var(--c-primary-fg)', cursor: 'pointer', padding: '4px 0' }}
                    title={t('tip.regionHit')}
                    onClick={() => { setForm(f => ({ ...f, region: r.name_he || r.name_en })); setRegionQuery(r.name_he || r.name_en); setRegionResults([]); }}>
                    {r.name_he || r.name_en}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <label style={label}>{t('substrate')}</label>
          <select style={input} value={form.substrate} onChange={set('substrate')} title={t('tip.substrate')}>
            <option value="">—</option>
            {SUBSTRATES.map(s => <option key={s} value={s}>{t(`collections:substrate.${s}`)}</option>)}
          </select>

          <label style={label}>{t('vegetation')}</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <input style={{ ...input, flex: 1 }} value={vegInput} onChange={e => setVegInput(e.target.value)} title={t('tip.vegetation')} />
            <button type="button" style={{ ...btn('var(--c-primary)'), width: 'auto', padding: '10px 16px' }} title={t('tip.addVegetation')} onClick={addVegetation}>{t('addVegetation')}</button>
          </div>
          <ul>{vegetation.map((v, i) => <li key={i}>{v}</li>)}</ul>
        </div>
      )}

      {step === 1 && (
        <div>
          <label style={label}>{t('size')}</label>
          <select style={input} value={form.fruiting_body_size} onChange={set('fruiting_body_size')} title={t('tip.size')}>
            <option value="">—</option>
            {SIZES.map(s => <option key={s} value={s}>{t(`collections:size.${s}`)}</option>)}
          </select>
          <label style={label}>{t('count')}</label>
          <input style={input} type="number" min="1" value={form.fruiting_body_count} onChange={set('fruiting_body_count')} title={t('tip.count')} />
          <label style={label}>{t('growthPattern')}</label>
          <select style={input} value={form.growth_pattern} onChange={set('growth_pattern')} title={t('tip.growthPattern')}>
            <option value="">—</option>
            {GROWTH_PATTERNS.map(g => <option key={g} value={g}>{t(`collections:growthPattern.${g}`)}</option>)}
          </select>
          <label style={label}>{t('stage')}</label>
          <select style={input} value={form.phenological_stage} onChange={set('phenological_stage')} title={t('tip.stage')}>
            <option value="">—</option>
            {STAGES.map(s => <option key={s} value={s}>{t(`collections:stage.${s}`)}</option>)}
          </select>
        </div>
      )}

      {step === 2 && (
        <div>
          <p>{t('identifyPrompt')}</p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <button type="button" onClick={() => setIdentifyChoice('propose')} title={t('tip.proposeSpecies')} style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid var(--c-primary)', background: identifyChoice === 'propose' ? 'var(--c-primary)' : 'var(--c-surface)', color: identifyChoice === 'propose' ? 'var(--c-on-accent)' : 'var(--c-primary-fg)', cursor: 'pointer' }}>{t('proposeSpecies')}</button>
            <button type="button" onClick={() => setIdentifyChoice('unknown')} title={t('tip.cantIdentify')} style={{ flex: 1, padding: 10, borderRadius: 6, border: '1px solid var(--c-text-muted)', background: identifyChoice === 'unknown' ? 'var(--c-text-muted)' : 'var(--c-surface)', color: identifyChoice === 'unknown' ? 'var(--c-on-accent)' : 'var(--c-text-muted-fg)', cursor: 'pointer' }}>{t('cantIdentify')}</button>
          </div>
          {identifyChoice === 'propose' && (
            <div>
              <label style={label}>{t('rank')}</label>
              <select style={input} value={form.current_rank} onChange={set('current_rank')} title={t('tip.rank')}>
                <option value="">—</option>
                {RANKS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <label style={label}>{t('taxonName')}</label>
              <input style={input} value={taxonQuery} onChange={e => searchTaxon(e.target.value)} disabled={!form.current_rank} title={t('tip.taxonName')} />
              {taxonSuggestions.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, marginTop: -8, marginBottom: 12 }}>
                  {taxonSuggestions.map(s => (
                    <li key={s.scientific_name}>
                      <button type="button" style={{ background: 'none', border: 'none', color: 'var(--c-primary-fg)', cursor: 'pointer', padding: '4px 0' }}
                        title={t('tip.taxonHit')}
                        onClick={() => { setForm(f => ({ ...f, current_taxon_name: s.scientific_name })); setTaxonQuery(s.scientific_name); setTaxonSuggestions([]); }}>
                        {s.scientific_name}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <label style={label}>{t('confidence')}</label>
              <select style={input} value={form.current_confidence} onChange={set('current_confidence')} title={t('tip.confidence')}>
                <option value="">—</option>
                <option value="certain">certain</option>
                <option value="uncertain">uncertain</option>
              </select>
            </div>
          )}
        </div>
      )}

      {step === 3 && sampleId && (
        <p style={{ fontSize: 13, color: 'var(--c-text-muted-fg)', margin: '0 0 8px' }} data-testid="survey-photos-hint">
          {t('photosHint')}
        </p>
      )}
      {step === 3 && sampleId && (
        <PhotoSection
          fetchUrl={`/collections/${sampleId}/photos`}
          uploadBody={{ sample_id: sampleId }}
          taxonName={form.current_taxon_name}
          sourceType="field_collection"
          photoTypes={['habitat', 'cap_top', 'fertile_layer', 'stipe', 'cross_section', 'other']}
        />
      )}
      {step === 3 && sampleId && (
        <SurveyAi sampleId={sampleId} chosen={identifyChoice === 'propose' ? form.current_taxon_name : null}
          onChoose={async (candidate) => {
            const proposal = { current_rank: candidate.rank, current_taxon_name: candidate.taxon_name,
              current_confidence: 'uncertain' };
            setForm(f => ({ ...f, ...proposal }));
            setTaxonQuery(candidate.taxon_name);
            setIdentifyChoice('propose');
            try {
              await saveDraftFields(sampleId, proposal);
            } catch {
              setError(t('submitError'));
            }
          }} />
      )}

      {step === 4 && (
        <div>
          <label style={label}>{t('notes')}</label>
          <textarea style={{ ...input, minHeight: 80 }} value={form.general_notes} onChange={set('general_notes')} title={t('tip.notes')} />
        </div>
      )}

      {error && <p style={{ color: 'var(--c-danger-2-fg)', fontSize: 13 }}>{error}</p>}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        {step > 0 && <button type="button" style={{ ...btn('var(--c-surface)'), color: 'var(--c-text-body-fg)', border: '1px solid var(--c-border-strong)' }} title={t('tip.back')} onClick={() => setStep(s => s - 1)}>{t('back')}</button>}
        {step < steps.length - 1 ? (
          <button type="button" disabled={!canAdvance()} style={btn(canAdvance() ? 'var(--c-primary)' : 'var(--c-text-faint)')} title={t('tip.next')} onClick={next}>{t('next')}</button>
        ) : (
          <button type="button" disabled={submitting} style={btn('var(--c-primary)')} title={t('tip.submit')} onClick={submitFinal}>{submitting ? t('submitting') : t('submit')}</button>
        )}
      </div>
    </div>
  );
}
